/**
 * Bundle Designer Modal
 * 
 * Full-featured modal for creating and editing billing / SLA templates.
 * Allows clients to design reusable product/service billing packages.
 * 
 * @version 1.0.0
 * @date December 2025
 */

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { 
  X, Plus, Trash2, Save, Package, GripVertical, Search,
  ChevronDown, Type, MessageSquare, Minus, Calculator,
  Star, Eye, Copy, AlertCircle, Check, Settings, Palette, Users, XCircle
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import toast from 'react-hot-toast';
import { authService } from '@/lib/authService';
import { isCapacitor } from '@/lib/platformUtils';
import {
  CONTACT_PAYMENT_TERMS_OPTIONS,
  formatContactPaymentTerms,
  getContactPaymentTermsOptionValue,
} from '@/lib/contacts/accountStanding';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ItemSearchModal, { type FinanceItem } from './ItemSearchModal';

type SlaPreset = {
  key: string;
  label: string;
  description: string;
  category: string;
  defaultPaymentTerms: string;
  defaultValidityDays: number;
  allowPriceOverride: boolean;
  applicableDocTypes: string[];
  defaultNotes: string;
  defaultTermsConditions: string;
};

// ============================================================================
// TYPES
// ============================================================================

interface BundleItem {
  id?: string;
  temp_id?: string; // For new items before save
  line_type: 'item' | 'header' | 'comment' | 'subtotal' | 'spacer';
  sort_order: number;
  work_item_id?: string;
  item_code?: string;
  item_name?: string;
  description?: string;
  quantity: number;
  unit: string;
  unit_price: number;
  unit_cost?: number;
  tax_rate_id?: number;
  tax_rate: number;
  discount_percentage?: number;
  discount_amount?: number;
  quantity_editable: boolean;
  price_editable: boolean;
  is_optional: boolean;
  is_required: boolean;
}

export interface Bundle {
  id?: string;
  name: string;
  description?: string;
  bundle_code?: string;
  applicable_doc_types: string[];
  category?: string;
  default_payment_terms?: string;
  default_notes?: string;
  default_terms_conditions?: string;
  default_validity_days?: number;
  bundle_price?: number;
  allow_price_override: boolean;
  assignment_scope?: 'shared' | 'selected_customers';
  default_contact_id?: number | null;
  is_active: boolean;
  is_featured: boolean;
  assigned_contact_ids?: number[];
  assigned_contacts?: Array<{ contact_id: number; contact_name: string }>;
  icon?: string;
  color?: string;
  items?: BundleItem[];
}

interface TaxRate {
  id: number;
  name: string;
  rate: number;
  is_default?: boolean;
}

interface CustomerAssignmentOption {
  id: number;
  company_name?: string | null;
  contact_person?: string | null;
  email?: string | null;
  contact_type?: string | null;
}

interface BundleDesignerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (bundle: Bundle) => void;
  bundle?: Bundle | null; // For editing
  contactId?: number;
  lockedContactId?: number | null;
  lockedContactLabel?: string;
}

const VALID_TEMPLATE_UNITS = [
  'kg', 'g', 'mg', 'oz', 'lb', 'ton',
  'l', 'ml', 'cup', 'tbsp', 'tsp', 'gallon', 'pint', 'quart',
  'piece', 'pcs', 'unit', 'each', 'item', 'box', 'pack', 'case', 'pallet',
  'm', 'cm', 'mm', 'ft', 'in', 'yard', 'sqm', 'sqft',
  'hour', 'day', 'week', 'month',
  'service', 'job', 'lot', 'roll', 'sheet', 'bundle', 'set', 'pair', 'tube',
] as const;

const TEMPLATE_UNIT_ALIASES: Record<string, string> = {
  units: 'unit',
  ea: 'each',
  pc: 'pcs',
  pcs: 'pcs',
  pieces: 'piece',
  hrs: 'hour',
  hr: 'hour',
  hours: 'hour',
  days: 'day',
  weeks: 'week',
  months: 'month',
  metres: 'm',
  metre: 'm',
  meters: 'm',
  meter: 'm',
  centimetres: 'cm',
  centimetre: 'cm',
  centimeters: 'cm',
  centimeter: 'cm',
  millimetres: 'mm',
  millimetre: 'mm',
  millimeters: 'mm',
  millimeter: 'mm',
  feet: 'ft',
  foot: 'ft',
  inches: 'in',
  inch: 'in',
  yards: 'yard',
  liters: 'l',
  liter: 'l',
  litres: 'l',
  litre: 'l',
  milliliters: 'ml',
  milliliter: 'ml',
  millilitres: 'ml',
  millilitre: 'ml',
  kilograms: 'kg',
  kilogram: 'kg',
  grams: 'g',
  gram: 'g',
  ounces: 'oz',
  ounce: 'oz',
  pounds: 'lb',
  pound: 'lb',
};

const normalizeTemplateUnit = (value: unknown): string => {
  if (typeof value !== 'string') {
    return 'unit';
  }

  const normalized = value.toLowerCase().trim();
  if (!normalized) {
    return 'unit';
  }

  if (VALID_TEMPLATE_UNITS.includes(normalized as (typeof VALID_TEMPLATE_UNITS)[number])) {
    return normalized;
  }

  return TEMPLATE_UNIT_ALIASES[normalized] || 'unit';
};

const normalizeNumericValue = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.trim());
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
};

const normalizeOptionalInteger = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const parsed = typeof value === 'number' ? value : Number.parseInt(String(value).trim(), 10);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeBundleItemForSave = (item: BundleItem, index: number): BundleItem => {
  const lineType = item.line_type || 'item';
  const normalizedDescription = item.description?.trim() || '';
  const normalizedItemName = item.item_name?.trim() || undefined;

  if (lineType === 'item') {
    return {
      ...item,
      sort_order: index,
      quantity: normalizeNumericValue(item.quantity, 1) > 0 ? normalizeNumericValue(item.quantity, 1) : 1,
      unit: normalizeTemplateUnit(item.unit),
      unit_price: normalizeNumericValue(item.unit_price, 0),
      unit_cost: item.unit_cost === null || item.unit_cost === undefined
        ? undefined
        : normalizeNumericValue(item.unit_cost, 0),
      tax_rate_id: normalizeOptionalInteger(item.tax_rate_id),
      tax_rate: normalizeNumericValue(item.tax_rate, 0),
      discount_percentage: normalizeNumericValue(item.discount_percentage, 0),
      discount_amount: normalizeNumericValue(item.discount_amount, 0),
      item_name: normalizedItemName || normalizedDescription || undefined,
      description: normalizedDescription || normalizedItemName || '',
      temp_id: undefined,
      id: item.id,
    };
  }

  return {
    ...item,
    sort_order: index,
    item_name: undefined,
    description: normalizedDescription,
    quantity: 0,
    unit: normalizeTemplateUnit(item.unit),
    unit_price: 0,
    unit_cost: undefined,
    tax_rate_id: undefined,
    tax_rate: 0,
    discount_percentage: 0,
    discount_amount: 0,
    temp_id: undefined,
    id: item.id,
  };
};

// ============================================================================
// CONSTANTS
// ============================================================================

const DOC_TYPE_OPTIONS = [
  { value: 'quote', label: 'Quote' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'proforma', label: 'Pro-forma' },
  { value: 'job', label: 'Job' },
  { value: 'sales_order', label: 'Sales Order' },
  { value: 'delivery_note', label: 'Delivery Note' },
  { value: 'credit_note', label: 'Credit Note' },
];

const CATEGORY_SUGGESTIONS = [
  'Plumbing', 'Electrical', 'HVAC', 'Painting', 'Construction',
  'Maintenance', 'Installation', 'Repair', 'Consulting', 'IT Services',
  'Security', 'Cleaning', 'Landscaping', 'Renovation', 'Other'
];

const COLOR_OPTIONS = [
  '#3b82f6', '#8b5cf6', '#ec4899', '#ef4444', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#6366f1'
];

const LEGACY_PAYMENT_TERM_MAP: Record<string, string> = {
  'due on receipt': 'cod',
  'cod': 'cod',
  'cash on delivery': 'cod',
  'cod (cash on delivery)': 'cod',
  'net 7': '7_days',
  'net 14': '14_days',
  'net 15': '14_days',
  'net 30': '30_days',
  'net 45': 'other',
  'net 60': '60_days',
  'net 90': '90_days',
  '50% deposit, balance on completion': 'other',
};

const normalizeBundlePaymentTerms = (value: unknown): string => {
  if (typeof value !== 'string') {
    return '30_days';
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '30_days';
  }

  const optionValue = getContactPaymentTermsOptionValue({ payment_terms: trimmed });
  if (optionValue) {
    return optionValue;
  }

  return LEGACY_PAYMENT_TERM_MAP[trimmed.toLowerCase()] || 'other';
};

const getBundlePaymentTermsSelection = (value: unknown): string => {
  if (typeof value !== 'string' || !value.trim()) {
    return '30_days';
  }

  const trimmed = value.trim();
  const knownOption = getContactPaymentTermsOptionValue({ payment_terms: trimmed });
  if (knownOption && knownOption !== 'other') {
    return knownOption;
  }

  const legacyMapped = LEGACY_PAYMENT_TERM_MAP[trimmed.toLowerCase()];
  return legacyMapped || 'other';
};

const getBundleCustomPaymentTerms = (value: unknown): string => {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  return getBundlePaymentTermsSelection(trimmed) === 'other' ? trimmed : '';
};

const getErrorMessage = (error: unknown, fallback: string): string => (
  error instanceof Error ? error.message : fallback
);

const SLA_PRESETS: SlaPreset[] = [
  {
    key: 'preventive-maintenance',
    label: 'Preventive SLA',
    description: 'Best for recurring planned maintenance contracts with included service lines and optional extras.',
    category: 'SLA - Preventive Maintenance',
    defaultPaymentTerms: '30_days',
    defaultValidityDays: 30,
    allowPriceOverride: false,
    applicableDocTypes: ['quote', 'invoice', 'proforma', 'job'],
    defaultNotes: 'Customer-specific pricing resolves when this template is applied to billing. Add catalog-backed service, callout, consumable, and labour items to build the SLA package.',
    defaultTermsConditions: 'Services and consumables are billed according to the active service level agreement, approved callout schedule, and any approved out-of-scope work.',
  },
  {
    key: 'callout-response',
    label: 'Callout SLA',
    description: 'Ideal for response-and-repair agreements with callout, travel, and labour charge components.',
    category: 'SLA - Callout Response',
    defaultPaymentTerms: 'cod',
    defaultValidityDays: 14,
    allowPriceOverride: true,
    applicableDocTypes: ['quote', 'invoice', 'job'],
    defaultNotes: 'Use TE-CALLOUT, TE-TRAVEL-TIME, TE-TRAVEL-KM, and labour service items for customer-resolved emergency billing templates.',
    defaultTermsConditions: 'Emergency response and callout charges are subject to the customer SLA, after-hours approval rules, and actual labour/travel consumption where applicable.',
  },
  {
    key: 'asset-service-pack',
    label: 'Asset Service Pack',
    description: 'Use for per-asset or per-unit service schedules such as heat pumps, generators, or HVAC packs.',
    category: 'SLA - Asset Service Pack',
    defaultPaymentTerms: '14_days',
    defaultValidityDays: 21,
    allowPriceOverride: false,
    applicableDocTypes: ['quote', 'invoice', 'sales_order', 'job'],
    defaultNotes: 'Set quantities to the covered asset count and keep catalog item links intact so billing can resolve the correct customer-specific price per unit.',
    defaultTermsConditions: 'Covered asset quantities, included consumables, and additional labour are billed in accordance with the contracted service schedule and approved variations.',
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

export default function BundleDesignerModal({
  isOpen,
  onClose,
  onSaved,
  bundle: existingBundle,
  contactId,
  lockedContactId = null,
  lockedContactLabel,
}: BundleDesignerModalProps) {
  const { currentTheme } = useTheme();
  const isDarkMode = currentTheme.mode === 'dark';
  const isEditing = !!existingBundle?.id;
  const isCapacitorApp = isCapacitor();
  const isLockedToCustomer = Number.isFinite(lockedContactId) && Number(lockedContactId) > 0;
  const normalizedLockedContactId = isLockedToCustomer ? Number(lockedContactId) : null;
  const safeAreaTop = 'max(env(safe-area-inset-top, 0px), var(--safe-area-top, 0px))';
  const safeAreaBottom = 'max(env(safe-area-inset-bottom, 0px), var(--safe-area-bottom, 0px))';

  // ============================================================================
  // STATE
  // ============================================================================

  const [activeTab, setActiveTab] = useState<'items' | 'settings'>('items');
  const [formData, setFormData] = useState<Partial<Bundle>>({
    name: '',
    description: '',
    bundle_code: '',
    applicable_doc_types: ['quote', 'invoice', 'job'],
    category: '',
    default_payment_terms: '30_days',
    default_notes: '',
    default_terms_conditions: '',
    default_validity_days: 30,
    allow_price_override: true,
    assignment_scope: 'shared',
    default_contact_id: null,
    assigned_contact_ids: [],
    is_active: true,
    is_featured: false,
    icon: 'Package',
    color: '#3b82f6'
  });

  const [items, setItems] = useState<BundleItem[]>([]);
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showItemSearch, setShowItemSearch] = useState(false);
  const [showInsertMenu, setShowInsertMenu] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [selectedItemIndices, setSelectedItemIndices] = useState<number[]>([]);
  const [customerOptions, setCustomerOptions] = useState<CustomerAssignmentOption[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customPaymentTerms, setCustomPaymentTerms] = useState('');

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Load existing bundle data
  useEffect(() => {
    if (isOpen && existingBundle) {
      const existingAssignedContactIds = existingBundle.assigned_contact_ids || existingBundle.assigned_contacts?.map((contact) => contact.contact_id) || [];
      setFormData({
        name: existingBundle.name,
        description: existingBundle.description || '',
        bundle_code: existingBundle.bundle_code || '',
        applicable_doc_types: existingBundle.applicable_doc_types || ['quote', 'invoice', 'job'],
        category: existingBundle.category || '',
        default_payment_terms: normalizeBundlePaymentTerms(existingBundle.default_payment_terms),
        default_notes: existingBundle.default_notes || '',
        default_terms_conditions: existingBundle.default_terms_conditions || '',
        default_validity_days: existingBundle.default_validity_days || 30,
        allow_price_override: existingBundle.allow_price_override ?? true,
        assignment_scope: isLockedToCustomer ? 'selected_customers' : (existingBundle.assignment_scope || 'shared'),
        default_contact_id: isLockedToCustomer ? normalizedLockedContactId : (existingBundle.default_contact_id ?? null),
        assigned_contact_ids: isLockedToCustomer ? [normalizedLockedContactId!] : existingAssignedContactIds,
        is_active: existingBundle.is_active ?? true,
        is_featured: existingBundle.is_featured ?? false,
        icon: existingBundle.icon || 'Package',
        color: existingBundle.color || '#3b82f6'
      });
      setCustomPaymentTerms(getBundleCustomPaymentTerms(existingBundle.default_payment_terms));
      setItems(existingBundle.items?.map(item => ({
        ...item,
        temp_id: item.id || `temp-${Date.now()}-${Math.random()}`
      })) || []);
    } else if (isOpen) {
      // Reset for new bundle
      setFormData({
        name: '',
        description: '',
        bundle_code: '',
        applicable_doc_types: ['quote', 'invoice', 'job'],
        category: '',
        default_payment_terms: '30_days',
        default_notes: '',
        default_terms_conditions: '',
        default_validity_days: 30,
        allow_price_override: true,
        assignment_scope: isLockedToCustomer ? 'selected_customers' : 'shared',
        default_contact_id: normalizedLockedContactId,
        assigned_contact_ids: normalizedLockedContactId != null ? [normalizedLockedContactId] : [],
        is_active: true,
        is_featured: false,
        icon: 'Package',
        color: '#3b82f6'
      });
      setCustomPaymentTerms('');
      setItems([]);
    }
  }, [isOpen, existingBundle, isLockedToCustomer, normalizedLockedContactId]);

  // Load tax rates
  useEffect(() => {
    if (isOpen) {
      fetchTaxRates();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;

    const loadCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const tokens = authService.getTokens();
        const params = new URLSearchParams({
          contact_types: 'customer',
          active: 'true',
          limit: '100',
        });

        if (customerSearch.trim()) {
          params.set('search', customerSearch.trim());
        }

        const response = await fetch(`/api/v1/contacts?${params.toString()}`, {
          headers: {
            'Authorization': `Bearer ${tokens?.access_token}`,
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success || cancelled) {
          return;
        }

        setCustomerOptions(Array.isArray(data.data) ? data.data : []);
      } catch (error) {
        console.error('Error loading customers for bundle assignment:', error);
      } finally {
        if (!cancelled) {
          setLoadingCustomers(false);
        }
      }
    };

    void loadCustomers();

    return () => {
      cancelled = true;
    };
  }, [customerSearch, isOpen]);
  async function fetchTaxRates() {
    try {
      const tokens = authService.getTokens();
      const response = await fetch('/api/v1/finance?action=getTaxRates', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          setTaxRates(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching tax rates:', error);
    }
  }

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setItems((prevItems) => {
        const oldIndex = Number(active.id);
        const newIndex = Number(over.id);
        return arrayMove(prevItems, oldIndex, newIndex);
      });
    }
  };

  const handleAddItem = (type: BundleItem['line_type']) => {
    const newItem: BundleItem = {
      temp_id: `temp-${Date.now()}-${Math.random()}`,
      line_type: type,
      sort_order: items.length,
      quantity: type === 'item' ? 1 : 0,
      unit: 'each',
      unit_price: 0,
      tax_rate: 15,
      quantity_editable: true,
      price_editable: true,
      is_optional: false,
      is_required: false,
      description: type === 'header' ? '' : type === 'comment' ? '' : type === 'subtotal' ? 'Subtotal' : ''
    };
    setItems(prev => [...prev, newItem]);
    setShowInsertMenu(false);
  };

  const handleAddProductItem = (product: FinanceItem) => {
    const defaultTaxRate = taxRates.find(t => t.is_default)?.rate || 15;
    const newItem: BundleItem = {
      temp_id: `temp-${Date.now()}-${Math.random()}`,
      line_type: 'item',
      sort_order: items.length,
      work_item_id: product.id,
      item_code: product.sku || product.item_code,
      item_name: product.name || product.description || '',
      description: product.description || product.name || '',
      quantity: 1,
      unit: normalizeTemplateUnit(product.unit_of_measure || 'each'),
      unit_price: parseFloat(String(product.unit_price || product.cost_price || 0)),
      unit_cost: parseFloat(String(product.cost_price || 0)),
      tax_rate: defaultTaxRate,
      quantity_editable: true,
      price_editable: true,
      is_optional: false,
      is_required: false
    };
    setItems(prev => [...prev, newItem]);
    setShowItemSearch(false);
  };

  const applySlaPreset = useCallback((preset: SlaPreset) => {
    setFormData(prev => ({
      ...prev,
      category: preset.category,
      default_payment_terms: preset.defaultPaymentTerms,
      default_validity_days: preset.defaultValidityDays,
      allow_price_override: preset.allowPriceOverride,
      applicable_doc_types: preset.applicableDocTypes,
      default_notes: prev.default_notes?.trim() ? prev.default_notes : preset.defaultNotes,
      default_terms_conditions: prev.default_terms_conditions?.trim()
        ? prev.default_terms_conditions
        : preset.defaultTermsConditions,
    }));

    setItems((prev) => {
      if (prev.length > 0) {
        return prev;
      }

      return [
        {
          temp_id: `temp-${Date.now()}-header`,
          line_type: 'header',
          sort_order: 0,
          quantity: 0,
          unit: 'each',
          unit_price: 0,
          tax_rate: 0,
          quantity_editable: false,
          price_editable: false,
          is_optional: false,
          is_required: true,
          description: `${preset.label} Billing Schedule`,
        },
        {
          temp_id: `temp-${Date.now()}-note`,
          line_type: 'comment',
          sort_order: 1,
          quantity: 0,
          unit: 'each',
          unit_price: 0,
          tax_rate: 0,
          quantity_editable: false,
          price_editable: false,
          is_optional: false,
          is_required: false,
          description: 'Add catalog-backed service items below so billing can resolve customer-specific prices when this template is applied.',
        },
      ];
    });

    toast.success(`${preset.label} preset applied`);
  }, []);

  const handleItemChange = <K extends keyof BundleItem>(index: number, field: K, value: BundleItem[K]) => {
    setItems(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const handleRemoveItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
    setSelectedItemIndices(prev => prev.filter(i => i !== index).map(i => i > index ? i - 1 : i));
  };

  const handleDeleteSelected = () => {
    setItems(prev => prev.filter((_, i) => !selectedItemIndices.includes(i)));
    setSelectedItemIndices([]);
  };

  const handleSave = async () => {
    const currentAssignedContactIds = Array.isArray(formData.assigned_contact_ids)
      ? formData.assigned_contact_ids
      : [];
    const enforcedAssignedContactIds = normalizedLockedContactId != null
      ? [normalizedLockedContactId]
      : currentAssignedContactIds;

    // Validation
    if (!formData.name?.trim()) {
      toast.error('Template name is required');
      return;
    }

    if (items.filter(i => i.line_type === 'item').length === 0) {
      toast.error('Add at least one product/service item');
      return;
    }

    if ((isLockedToCustomer || formData.assignment_scope === 'selected_customers') && enforcedAssignedContactIds.length === 0) {
      toast.error('Select at least one customer for a customer-assigned template');
      return;
    }

    const paymentTermsSelection = getBundlePaymentTermsSelection(formData.default_payment_terms);
    const resolvedPaymentTerms = paymentTermsSelection === 'other'
      ? (customPaymentTerms.trim() || 'other')
      : (formData.default_payment_terms || '30_days');

    setIsSaving(true);
    try {
      const tokens = authService.getTokens();
      
      // Prepare items for save
      const preparedItems = items.map((item, index) => normalizeBundleItemForSave(item, index));

      const payload = {
        ...formData,
        default_payment_terms: resolvedPaymentTerms,
        assignment_scope: isLockedToCustomer ? 'selected_customers' : (formData.assignment_scope || 'shared'),
        assigned_contact_ids: isLockedToCustomer
          ? enforcedAssignedContactIds
          : (formData.assignment_scope === 'selected_customers' ? currentAssignedContactIds : []),
        default_contact_id: isLockedToCustomer
          ? normalizedLockedContactId
          : formData.assignment_scope === 'selected_customers'
          ? (formData.default_contact_id ?? currentAssignedContactIds[0] ?? null)
          : null,
        items: preparedItems
      };

      const url = isEditing 
        ? `/api/v1/work-items/bundles/${existingBundle?.id}`
        : '/api/v1/work-items/bundles';
      
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${tokens?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to save template');
      }

      toast.success(isEditing ? 'Template updated!' : 'Template created!');
      onSaved?.(data.bundle);
      onClose();

    } catch (error: unknown) {
      console.error('Error saving template:', error);
      toast.error(getErrorMessage(error, 'Failed to save template'));
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================================================
  // CALCULATIONS
  // ============================================================================

  const calculateSubtotal = () => {
    return items
      .filter(item => item.line_type === 'item')
      .reduce((sum, item) => sum + ((item.quantity || 0) * (item.unit_price || 0)), 0);
  };

  const calculateTax = () => {
    return items
      .filter(item => item.line_type === 'item')
      .reduce((sum, item) => {
        const lineTotal = (item.quantity || 0) * (item.unit_price || 0);
        return sum + (lineTotal * (item.tax_rate || 0) / 100);
      }, 0);
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-ZA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const assignedContactIds = Array.isArray(formData.assigned_contact_ids)
    ? formData.assigned_contact_ids
    : [];
  const paymentTermsSelection = getBundlePaymentTermsSelection(formData.default_payment_terms);
  const paymentTermsDisplayValue = paymentTermsSelection === 'other'
    ? (customPaymentTerms.trim() || formData.default_payment_terms || 'other')
    : (formData.default_payment_terms || '30_days');

  const selectedCustomers = assignedContactIds.map((contactId) => {
    const matchingOption = customerOptions.find((customer) => Number(customer.id) === Number(contactId));
    const existingAssigned = existingBundle?.assigned_contacts?.find((contact) => Number(contact.contact_id) === Number(contactId));

    return {
      contact_id: Number(contactId),
      contact_name: matchingOption?.company_name || matchingOption?.contact_person || existingAssigned?.contact_name || `Contact ${contactId}`,
      email: matchingOption?.email || null,
    };
  });

  const currentBillingCustomer = contactId != null
    ? selectedCustomers.find((customer) => customer.contact_id === Number(contactId))
      || (() => {
        const matchingOption = customerOptions.find((customer) => Number(customer.id) === Number(contactId));

        if (!matchingOption) {
          return null;
        }

        return {
          contact_id: Number(contactId),
          contact_name: matchingOption.company_name || matchingOption.contact_person || `Contact ${contactId}`,
          email: matchingOption.email || null,
        };
      })()
    : null;

  const isAssignedToCurrentBillingCustomer = contactId != null
    && assignedContactIds.includes(Number(contactId));

  const toggleAssignedContact = (contactId: number) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.assigned_contact_ids) ? prev.assigned_contact_ids : [];
      const exists = current.includes(contactId);
      const nextAssigned = exists ? current.filter((value) => value !== contactId) : [...current, contactId];
      const nextDefaultContactId = prev.default_contact_id === contactId && exists
        ? (nextAssigned[0] ?? null)
        : (prev.default_contact_id ?? (!exists ? contactId : null));

      return {
        ...prev,
        assigned_contact_ids: nextAssigned,
        default_contact_id: nextDefaultContactId,
      };
    });
  };

  const makeTemplateCustomerSpecific = () => {
    if (isLockedToCustomer) {
      setActiveTab('settings');
      return;
    }

    if (contactId == null) {
      setActiveTab('settings');
      return;
    }

    const normalizedContactId = Number(contactId);

    setFormData((prev) => {
      const currentAssigned = Array.isArray(prev.assigned_contact_ids) ? prev.assigned_contact_ids : [];
      const nextAssigned = currentAssigned.includes(normalizedContactId)
        ? currentAssigned
        : [...currentAssigned, normalizedContactId];

      return {
        ...prev,
        assignment_scope: 'selected_customers',
        assigned_contact_ids: nextAssigned,
        default_contact_id: prev.default_contact_id ?? normalizedContactId,
      };
    });

    setActiveTab('settings');
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!isOpen) return null;

  return (
    <>
      <div className={`fixed inset-0 z-50 bg-black/60 backdrop-blur-sm ${isCapacitorApp ? 'overflow-hidden p-0' : 'flex items-center justify-center overflow-y-auto p-4'}`}>
        <div className={`relative w-full shadow-2xl ${
          isDarkMode ? 'bg-gray-900' : 'bg-white'
        } ${isCapacitorApp ? 'h-[100dvh] max-h-[100dvh] rounded-none' : 'max-w-6xl my-8 rounded-xl'}`}>
          {isCapacitorApp && (
            <div
              className={isDarkMode ? 'bg-gray-900' : 'bg-white'}
              style={{ height: safeAreaTop }}
              aria-hidden="true"
            />
          )}
          {/* Header */}
          <div 
            className={`px-6 py-4 text-white flex items-center justify-between ${isCapacitorApp ? '' : 'rounded-t-xl'}`}
            style={{ backgroundColor: formData.color || '#3b82f6' }}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 backdrop-blur rounded-lg">
                <Package className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold">
                  {isEditing ? 'Edit Billing Template' : 'Create Billing Template'}
                </h2>
                <p className="text-white/80 text-sm">
                  Design reusable billing and SLA packages for quotes, invoices, and job billing
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Tabs */}
          <div className={`flex border-b ${
            isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
          }`}>
            <button
              onClick={() => setActiveTab('items')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'items'
                  ? 'border-purple-500 text-purple-600'
                  : `border-transparent ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
              }`}
            >
              <div className="flex items-center gap-2">
                <Package size={16} />
                Billing Lines
                {items.length > 0 && (
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                  }`}>
                    {items.filter(i => i.line_type === 'item').length}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'settings'
                  ? 'border-purple-500 text-purple-600'
                  : `border-transparent ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
              }`}
            >
              <div className="flex items-center gap-2">
                <Settings size={16} />
                Settings
              </div>
            </button>
          </div>

          {/* Content */}
          <div
            className="p-6"
            style={{
              maxHeight: isCapacitorApp ? `calc(var(--100dvh, 100vh) - ${safeAreaTop} - ${safeAreaBottom} - 145px)` : 'calc(100vh - 280px)',
              overflowY: 'auto',
            }}
          >
            {activeTab === 'items' ? (
              <div className="space-y-4">
                {contactId != null && (
                  <div className={`rounded-xl border p-4 ${isDarkMode ? 'border-blue-800/70 bg-blue-950/20' : 'border-blue-200 bg-blue-50'}`}>
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-blue-200' : 'text-blue-900'}`}>
                          {isLockedToCustomer ? 'Customer-locked template' : 'Customer-specific template'}
                        </h3>
                        <p className={`mt-1 text-xs leading-5 ${isDarkMode ? 'text-blue-100/80' : 'text-blue-700'}`}>
                          {isLockedToCustomer
                            ? `Templates created from this stakeholder view are limited to ${lockedContactLabel || `Customer ${normalizedLockedContactId}`}. Use the finance workspace to create shared or multi-customer templates.`
                            : isAssignedToCurrentBillingCustomer
                            ? `This template is currently set up for selected customers${currentBillingCustomer ? `, including ${currentBillingCustomer.contact_name}` : ''}.`
                            : currentBillingCustomer
                              ? `Creating this from billing for ${currentBillingCustomer.contact_name}. Use customer assignment if this template should only appear for that customer.`
                              : 'You can limit this template to the current billing customer from the settings tab.'}
                        </p>
                      </div>

                      {!isLockedToCustomer && (
                        <button
                          type="button"
                          onClick={makeTemplateCustomerSpecific}
                          className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition ${
                            isDarkMode
                              ? 'bg-blue-600 text-white hover:bg-blue-500'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          <Users className="h-3.5 w-3.5" />
                          {isAssignedToCurrentBillingCustomer ? 'Review customer assignment' : 'Assign to this customer'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Template Name & Code */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className={`block text-sm font-medium mb-1 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Billing Template Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Geyser Installation - Standard"
                      className={`w-full px-4 py-2 rounded-lg border ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      } focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Code
                    </label>
                    <input
                      type="text"
                      value={formData.bundle_code || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, bundle_code: e.target.value.toUpperCase() }))}
                      placeholder="GEY-STD"
                      maxLength={20}
                      className={`w-full px-4 py-2 rounded-lg border font-mono ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      } focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                    />
                  </div>
                </div>

                <div className={`rounded-xl border p-4 ${
                  isDarkMode ? 'border-gray-700 bg-gray-800/80' : 'border-purple-100 bg-purple-50/70'
                }`}>
                  <div className="mb-3 flex items-center gap-2">
                    <Palette className="h-4 w-4 text-purple-500" />
                    <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      SLA starter presets
                    </h3>
                  </div>
                  <div className="grid gap-2 md:grid-cols-3">
                    {SLA_PRESETS.map((preset) => (
                      <button
                        key={preset.key}
                        type="button"
                        onClick={() => applySlaPreset(preset)}
                        className={`rounded-lg border p-3 text-left transition ${
                          isDarkMode
                            ? 'border-gray-700 bg-gray-900/70 hover:border-purple-500 hover:bg-gray-900'
                            : 'border-white bg-white hover:border-purple-300 hover:shadow-sm'
                        }`}
                      >
                        <div className="mb-1 flex items-center gap-2">
                          <Star className="h-3.5 w-3.5 text-purple-500" />
                          <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {preset.label}
                          </span>
                        </div>
                        <p className={`text-xs leading-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {preset.description}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {/* Add Item Button */}
                    <button
                      type="button"
                      onClick={() => setShowItemSearch(true)}
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium ${
                        isDarkMode 
                          ? 'bg-purple-600 text-white hover:bg-purple-700' 
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                      }`}
                    >
                      <Plus size={16} />
                      Add Product/Service
                    </button>

                    {/* Insert Dropdown */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowInsertMenu(!showInsertMenu)}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium ${
                          isDarkMode 
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Insert
                        <ChevronDown size={16} />
                      </button>
                      
                      {showInsertMenu && (
                        <div className={`absolute top-full left-0 mt-1 w-48 rounded-lg shadow-lg border z-10 ${
                          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                          <button
                            type="button"
                            onClick={() => handleAddItem('header')}
                            className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                              isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                            }`}
                          >
                            <Type size={16} className="text-purple-500" />
                            Section Header
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAddItem('comment')}
                            className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                              isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                            }`}
                          >
                            <MessageSquare size={16} className="text-yellow-500" />
                            Note/Comment
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAddItem('spacer')}
                            className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                              isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                            }`}
                          >
                            <Minus size={16} className="text-gray-500" />
                            Spacer
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAddItem('subtotal')}
                            className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                              isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                            }`}
                          >
                            <Calculator size={16} className="text-blue-500" />
                            Subtotal Line
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={`rounded-lg px-3 py-2 text-xs ${
                    isDarkMode ? 'bg-blue-900/20 text-blue-300' : 'bg-blue-50 text-blue-700'
                  }`}>
                    Catalog-linked service items resolve customer pricing when this template is applied from billing.
                  </div>

                  {selectedItemIndices.length > 0 && (
                    <button
                      type="button"
                      onClick={handleDeleteSelected}
                      className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 ${
                        isDarkMode 
                          ? 'text-red-400 hover:bg-red-900/30' 
                          : 'text-red-600 hover:bg-red-50'
                      }`}
                    >
                      <Trash2 size={14} />
                      Delete Selected ({selectedItemIndices.length})
                    </button>
                  )}
                </div>

                {/* Items Table */}
                <div className={`border rounded-lg overflow-hidden ${
                  isDarkMode ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <table className="w-full">
                      <thead className={`border-b ${
                        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <tr>
                          <th className="px-2 py-2 w-12"></th>
                          <th className={`px-2 py-2 text-left text-xs font-semibold uppercase ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>Description</th>
                          <th className={`px-2 py-2 text-center text-xs font-semibold uppercase w-20 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>Qty</th>
                          <th className={`px-2 py-2 text-right text-xs font-semibold uppercase w-28 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>Price</th>
                          <th className={`px-2 py-2 text-center text-xs font-semibold uppercase w-20 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>VAT %</th>
                          <th className={`px-2 py-2 text-right text-xs font-semibold uppercase w-28 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>Total</th>
                          <th className={`px-2 py-2 text-center text-xs font-semibold uppercase w-20 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>Options</th>
                          <th className="px-2 py-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody className={isDarkMode ? 'bg-gray-800/50' : ''}>
                        <SortableContext
                          items={items.map((_, idx) => idx)}
                          strategy={verticalListSortingStrategy}
                        >
                          {items.length === 0 ? (
                            <tr>
                              <td colSpan={8} className={`px-4 py-12 text-center ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                <Package className={`mx-auto mb-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} size={32} />
                                <p>No items added yet</p>
                                <p className="text-sm mt-1">Click "Add Product/Service" to get started</p>
                              </td>
                            </tr>
                          ) : (
                            items.map((item, index) => (
                              <SortableItemRow
                                key={item.temp_id || item.id || index}
                                item={item}
                                index={index}
                                isSelected={selectedItemIndices.includes(index)}
                                formatCurrency={formatCurrency}
                                onItemChange={handleItemChange}
                                onRemove={handleRemoveItem}
                                onToggleSelection={(idx) => {
                                  setSelectedItemIndices(prev =>
                                    prev.includes(idx)
                                      ? prev.filter(i => i !== idx)
                                      : [...prev, idx]
                                  );
                                }}
                                taxRates={taxRates}
                                isDarkMode={isDarkMode}
                              />
                            ))
                          )}
                        </SortableContext>
                      </tbody>
                    </table>
                  </DndContext>
                </div>

                {/* Totals */}
                {items.length > 0 && (
                  <div className={`flex justify-end`}>
                    <div className={`w-72 rounded-lg p-4 ${
                      isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
                    }`}>
                      <div className="flex justify-between py-2">
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Subtotal</span>
                        <span className={`font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          R {formatCurrency(calculateSubtotal())}
                        </span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>VAT</span>
                        <span className={`font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          R {formatCurrency(calculateTax())}
                        </span>
                      </div>
                      <div className={`flex justify-between py-2 border-t font-bold ${
                        isDarkMode ? 'border-gray-700' : 'border-gray-300'
                      }`}>
                        <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>Total</span>
                        <span className={`font-mono text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          R {formatCurrency(calculateSubtotal() + calculateTax())}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Settings Tab */
              <div className="space-y-6">
                {/* Description */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Description
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what this template includes..."
                    rows={2}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    } focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none`}
                  />
                </div>

                {/* Category & Document Types */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <label className={`block text-sm font-medium mb-1 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Category
                    </label>
                    <input
                      type="text"
                      value={formData.category || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      onFocus={() => setShowCategoryDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 150)}
                      placeholder="e.g., Plumbing, Electrical"
                      className={`w-full px-4 py-2 rounded-lg border ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      } focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                    />
                    {showCategoryDropdown && (
                      <div className={`absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto rounded-lg shadow-lg border z-10 ${
                        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                      }`}>
                        {CATEGORY_SUGGESTIONS.filter(c => 
                          c.toLowerCase().includes((formData.category || '').toLowerCase())
                        ).map(category => (
                          <button
                            key={category}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, category }));
                              setShowCategoryDropdown(false);
                            }}
                            className={`w-full px-4 py-2 text-left text-sm ${
                              isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                            }`}
                          >
                            {category}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Applies To
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {DOC_TYPE_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              applicable_doc_types: prev.applicable_doc_types?.includes(opt.value)
                                ? prev.applicable_doc_types.filter(t => t !== opt.value)
                                : [...(prev.applicable_doc_types || []), opt.value]
                            }));
                          }}
                          className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                            formData.applicable_doc_types?.includes(opt.value)
                              ? 'bg-purple-600 text-white'
                              : isDarkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Default Values */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Default Payment Terms
                    </label>
                    <select
                      value={paymentTermsSelection}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          default_payment_terms: nextValue === 'other'
                            ? (customPaymentTerms.trim() || 'other')
                            : nextValue,
                        }));
                      }}
                      className={`w-full px-4 py-2 rounded-lg border ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-700 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      } focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                    >
                      <option value="">Select...</option>
                      {CONTACT_PAYMENT_TERMS_OPTIONS.map((term) => (
                        <option key={term.value} value={term.value}>{term.label}</option>
                      ))}
                    </select>
                    {paymentTermsSelection === 'other' && (
                      <input
                        type="text"
                        value={customPaymentTerms}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setCustomPaymentTerms(nextValue);
                          setFormData((prev) => ({
                            ...prev,
                            default_payment_terms: nextValue.trim() || 'other',
                          }));
                        }}
                        placeholder="Specify custom payment terms"
                        className={`mt-2 w-full rounded-lg border px-4 py-2 text-sm ${
                          isDarkMode
                            ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                        } focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                      />
                    )}
                    <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Uses the same payment terms model as stakeholder account settings: {formatContactPaymentTerms({ payment_terms: paymentTermsDisplayValue })}
                    </p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Validity (days)
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={formData.default_validity_days || 30}
                      onChange={(e) => setFormData(prev => ({ ...prev, default_validity_days: parseInt(e.target.value) || 30 }))}
                      className={`w-full px-4 py-2 rounded-lg border ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-700 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      } focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Bundle Target Price
                    </label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={formData.bundle_price ?? ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        bundle_price: e.target.value === '' ? undefined : parseFloat(e.target.value),
                      }))}
                      placeholder="Optional commercial target"
                      className={`w-full px-4 py-2 rounded-lg border font-mono ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      } focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                    />
                  </div>
                </div>

                <div className={`rounded-xl border p-4 ${isDarkMode ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-start gap-3">
                    <AlertCircle className={`mt-0.5 h-4 w-4 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`} />
                    <div className="space-y-2">
                      <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        SLA pricing behaviour
                      </h3>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.allow_price_override ?? true}
                          onChange={(e) => setFormData(prev => ({ ...prev, allow_price_override: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Allow billing users to override template pricing after application
                        </span>
                      </label>
                      <p className={`text-xs leading-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Disable overrides for contract-driven SLA packs where customer-specific prices should be resolved from linked catalog items and retained as-is.
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`rounded-xl border p-4 ${isDarkMode ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-start gap-3">
                    <Users className={`mt-0.5 h-4 w-4 ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`} />
                    <div className="w-full space-y-3">
                      <div>
                        <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Customer assignment
                        </h3>
                        <p className={`mt-1 text-xs leading-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Use a shared template when the structure stays the same for everyone, or assign it to selected customers when the whole SLA/template should only be available for specific accounts.
                        </p>
                      </div>

                      {isLockedToCustomer ? (
                        <div className={`rounded-lg border p-3 ${isDarkMode ? 'border-purple-800/60 bg-purple-950/30' : 'border-purple-200 bg-purple-50'}`}>
                          <div className={`text-sm font-semibold ${isDarkMode ? 'text-purple-200' : 'text-purple-900'}`}>
                            Assigned only to {lockedContactLabel || `Customer ${normalizedLockedContactId}`}
                          </div>
                          <div className={`mt-1 text-xs leading-5 ${isDarkMode ? 'text-purple-100/80' : 'text-purple-700'}`}>
                            This stakeholder view creates and edits templates only for this customer. Use the finance workspace if you need a shared template or a template assigned to multiple customers.
                          </div>
                        </div>
                      ) : (
                      <div className="grid gap-2 md:grid-cols-2">
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({
                            ...prev,
                            assignment_scope: 'shared',
                            default_contact_id: null,
                            assigned_contact_ids: [],
                          }))}
                          className={`rounded-lg border p-3 text-left transition ${
                            (formData.assignment_scope || 'shared') === 'shared'
                              ? 'border-purple-500 bg-purple-600 text-white'
                              : isDarkMode
                                ? 'border-gray-700 bg-gray-900/70 text-gray-300 hover:border-purple-500'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-purple-300'
                          }`}
                        >
                          <div className="text-sm font-semibold">Shared template</div>
                          <div className={`mt-1 text-xs ${(formData.assignment_scope || 'shared') === 'shared' ? 'text-white/80' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Available for all customers. Linked catalog lines will still resolve customer-specific prices when applied.
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({
                            ...prev,
                            assignment_scope: 'selected_customers',
                            assigned_contact_ids: Array.isArray(prev.assigned_contact_ids) ? prev.assigned_contact_ids : [],
                            default_contact_id: prev.default_contact_id ?? null,
                          }))}
                          className={`rounded-lg border p-3 text-left transition ${
                            formData.assignment_scope === 'selected_customers'
                              ? 'border-purple-500 bg-purple-600 text-white'
                              : isDarkMode
                                ? 'border-gray-700 bg-gray-900/70 text-gray-300 hover:border-purple-500'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-purple-300'
                          }`}
                        >
                          <div className="text-sm font-semibold">Assigned customers only</div>
                          <div className={`mt-1 text-xs ${formData.assignment_scope === 'selected_customers' ? 'text-white/80' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Only the selected customers will see and apply this template in billing.
                          </div>
                        </button>
                      </div>
                      )}

                      {!isLockedToCustomer && formData.assignment_scope === 'selected_customers' && (
                        <div className={`rounded-lg border p-3 ${isDarkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-white'}`}>
                          <div className="flex flex-col gap-3">
                            <input
                              type="text"
                              value={customerSearch}
                              onChange={(e) => setCustomerSearch(e.target.value)}
                              placeholder="Search customers to assign..."
                              className={`w-full rounded-lg border px-3 py-2 text-sm ${
                                isDarkMode
                                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                              } focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                            />

                            {selectedCustomers.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {selectedCustomers.map((customer) => (
                                  <span
                                    key={customer.contact_id}
                                    className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${isDarkMode ? 'bg-purple-900/30 text-purple-200' : 'bg-purple-100 text-purple-700'}`}
                                  >
                                    {customer.contact_name}
                                    <button
                                      type="button"
                                      onClick={() => toggleAssignedContact(customer.contact_id)}
                                      className="rounded-full hover:bg-black/10"
                                    >
                                      <XCircle className="h-3.5 w-3.5" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}

                            <div className={`max-h-52 overflow-y-auto rounded-lg border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                              {loadingCustomers ? (
                                <div className={`px-3 py-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading customers…</div>
                              ) : customerOptions.length === 0 ? (
                                <div className={`px-3 py-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No matching customers found.</div>
                              ) : (
                                customerOptions.map((customer) => {
                                  const customerName = customer.company_name || customer.contact_person || `Contact ${customer.id}`;
                                  const isSelected = assignedContactIds.includes(Number(customer.id));

                                  return (
                                    <button
                                      key={customer.id}
                                      type="button"
                                      onClick={() => toggleAssignedContact(Number(customer.id))}
                                      className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition ${
                                        isSelected
                                          ? isDarkMode ? 'bg-purple-900/30 text-purple-200' : 'bg-purple-50 text-purple-700'
                                          : isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-50'
                                      }`}
                                    >
                                      <div>
                                        <div className="font-medium">{customerName}</div>
                                        {(customer.email || customer.contact_type) && (
                                          <div className={`text-xs ${isSelected ? isDarkMode ? 'text-purple-300/80' : 'text-purple-600/80' : isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                            {[customer.email, customer.contact_type].filter(Boolean).join(' · ')}
                                          </div>
                                        )}
                                      </div>
                                      {isSelected && <Check className="h-4 w-4" />}
                                    </button>
                                  );
                                })
                              )}
                            </div>

                            <div>
                              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                Default customer for this template
                              </label>
                              <select
                                value={formData.default_contact_id ?? ''}
                                onChange={(e) => setFormData((prev) => ({
                                  ...prev,
                                  default_contact_id: e.target.value ? Number(e.target.value) : null,
                                }))}
                                className={`w-full rounded-lg border px-3 py-2 text-sm ${
                                  isDarkMode
                                    ? 'bg-gray-800 border-gray-700 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                                } focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                              >
                                <option value="">No default customer</option>
                                {selectedCustomers.map((customer) => (
                                  <option key={customer.contact_id} value={customer.contact_id}>
                                    {customer.contact_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Default Notes */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Default Notes
                  </label>
                  <textarea
                    value={formData.default_notes || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, default_notes: e.target.value }))}
                    placeholder="Notes to include on documents using this template..."
                    rows={3}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    } focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none`}
                  />
                </div>

                {/* Terms & Conditions */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Default Terms & Conditions
                  </label>
                  <textarea
                    value={formData.default_terms_conditions || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, default_terms_conditions: e.target.value }))}
                    placeholder="Terms and conditions to include..."
                    rows={3}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    } focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none`}
                  />
                </div>

                {/* Options & Color */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Template Color
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {COLOR_OPTIONS.map(color => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, color }))}
                          className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                            formData.color === color ? 'border-white ring-2 ring-offset-2 ring-purple-500' : 'border-transparent'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Options
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Active (available for use)
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_featured}
                          onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Featured (show at top of list)
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={`px-6 py-4 border-t flex items-center justify-between ${
            isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
          } ${isCapacitorApp ? '' : 'rounded-b-xl'}`}>
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                isDarkMode 
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  {isEditing ? 'Update Template' : 'Save Template'}
                </>
              )}
            </button>
          </div>

          {isCapacitorApp && (
            <div
              className={isDarkMode ? 'bg-gray-900' : 'bg-white'}
              style={{ height: safeAreaBottom }}
              aria-hidden="true"
            />
          )}
        </div>
      </div>

      {/* Item Search Modal */}
      {showItemSearch && (
        <ItemSearchModal
          isOpen={showItemSearch}
          onClose={() => setShowItemSearch(false)}
          onSelect={(item) => handleAddProductItem(item)}
          documentType="sales"
          contactId={contactId}
        />
      )}
    </>
  );
}

// ============================================================================
// SORTABLE ITEM ROW
// ============================================================================

interface SortableItemRowProps {
  item: BundleItem;
  index: number;
  isSelected: boolean;
  formatCurrency: (value: number) => string;
  onItemChange: <K extends keyof BundleItem>(index: number, field: K, value: BundleItem[K]) => void;
  onRemove: (index: number) => void;
  onToggleSelection: (index: number) => void;
  taxRates: TaxRate[];
  isDarkMode: boolean;
}

function SortableItemRow({
  item,
  index,
  isSelected,
  formatCurrency,
  onItemChange,
  onRemove,
  onToggleSelection,
  taxRates,
  isDarkMode
}: SortableItemRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const lineType = item.line_type || 'item';

  // Header row
  if (lineType === 'header') {
    return (
      <tr
        ref={setNodeRef}
        style={style}
        className={`border-b ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-100'} ${isSelected ? 'ring-2 ring-purple-400' : ''}`}
      >
        <td className="px-2 py-2">
          <button {...attributes} {...listeners} className="cursor-grab p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
            <GripVertical size={14} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
          </button>
        </td>
        <td colSpan={6} className="px-2 py-2">
          <input
            type="text"
            value={item.description || ''}
            onChange={(e) => onItemChange(index, 'description', e.target.value)}
            placeholder="Section header..."
            className={`w-full px-3 py-2 text-sm font-bold border-0 bg-transparent focus:ring-0 outline-none uppercase tracking-wide ${
              isDarkMode ? 'text-gray-200 placeholder-gray-500' : 'text-gray-800 placeholder-gray-400'
            }`}
          />
        </td>
        <td className="px-2 py-2">
          <button onClick={() => onRemove(index)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded">
            <Trash2 size={14} />
          </button>
        </td>
      </tr>
    );
  }

  // Comment row
  if (lineType === 'comment') {
    return (
      <tr
        ref={setNodeRef}
        style={style}
        className={`border-b ${isDarkMode ? 'border-gray-700 bg-yellow-900/20' : 'border-gray-100 bg-amber-50/50'} ${isSelected ? 'ring-2 ring-purple-400' : ''}`}
      >
        <td className="px-2 py-2">
          <button {...attributes} {...listeners} className="cursor-grab p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
            <GripVertical size={14} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
          </button>
        </td>
        <td colSpan={6} className="px-2 py-2">
          <textarea
            value={item.description || ''}
            onChange={(e) => onItemChange(index, 'description', e.target.value)}
            placeholder="Add a note..."
            rows={2}
            className={`w-full px-3 py-2 text-sm italic border rounded resize-none ${
              isDarkMode 
                ? 'bg-gray-800 border-gray-600 text-gray-300 placeholder-gray-500' 
                : 'bg-white border-amber-200 text-gray-600 placeholder-gray-400'
            } focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none`}
          />
        </td>
        <td className="px-2 py-2">
          <button onClick={() => onRemove(index)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded">
            <Trash2 size={14} />
          </button>
        </td>
      </tr>
    );
  }

  // Subtotal row
  if (lineType === 'subtotal') {
    return (
      <tr
        ref={setNodeRef}
        style={style}
        className={`border-b ${isDarkMode ? 'border-gray-700 bg-blue-900/20' : 'border-gray-200 bg-blue-50/50'} ${isSelected ? 'ring-2 ring-purple-400' : ''}`}
      >
        <td className="px-2 py-2">
          <button {...attributes} {...listeners} className="cursor-grab p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
            <GripVertical size={14} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
          </button>
        </td>
        <td colSpan={4} className="px-2 py-2">
          <input
            type="text"
            value={item.description || 'Subtotal'}
            onChange={(e) => onItemChange(index, 'description', e.target.value)}
            className={`w-full px-3 py-2 text-sm font-semibold border-0 bg-transparent focus:ring-0 outline-none text-right ${
              isDarkMode ? 'text-blue-300' : 'text-blue-700'
            }`}
          />
        </td>
        <td className="px-2 py-2 text-right">
          <span className={`text-sm font-bold font-mono ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>—</span>
        </td>
        <td></td>
        <td className="px-2 py-2">
          <button onClick={() => onRemove(index)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded">
            <Trash2 size={14} />
          </button>
        </td>
      </tr>
    );
  }

  // Spacer row
  if (lineType === 'spacer') {
    return (
      <tr
        ref={setNodeRef}
        style={style}
        className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} ${isSelected ? 'ring-2 ring-purple-400' : ''}`}
      >
        <td className="px-2 py-1">
          <button {...attributes} {...listeners} className="cursor-grab p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
            <GripVertical size={14} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
          </button>
        </td>
        <td colSpan={6} className="px-2 py-1 text-center">
          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>— spacer —</span>
        </td>
        <td className="px-2 py-1">
          <button onClick={() => onRemove(index)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded">
            <Trash2 size={14} />
          </button>
        </td>
      </tr>
    );
  }

  // Default: item row
  const lineTotal = (item.quantity || 0) * (item.unit_price || 0);

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-b ${isDarkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-100 hover:bg-gray-50'} ${isSelected ? 'bg-purple-50 dark:bg-purple-900/20' : ''}`}
    >
      <td className="px-2 py-2">
        <div className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelection(index)}
            className="w-4 h-4 rounded border-gray-300 text-purple-600"
          />
          <button {...attributes} {...listeners} className="cursor-grab p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
            <GripVertical size={14} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
          </button>
        </div>
      </td>
      <td className="px-2 py-2">
        <input
          type="text"
          value={item.description || item.item_name || ''}
          onChange={(e) => onItemChange(index, 'description', e.target.value)}
          placeholder="Description"
          className={`w-full px-2 py-1.5 text-sm border rounded ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' 
              : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
          } focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none`}
        />
        {item.item_code && (
          <span className={`text-xs mt-1 font-mono ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {item.item_code}
          </span>
        )}
        {item.work_item_id && (
          <span className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
            isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'
          }`}>
            <Check className="h-3 w-3" />
            Customer pricing ready
          </span>
        )}
      </td>
      <td className="px-2 py-2">
        <input
          type="number"
          min="0"
          step="1"
          value={item.quantity || ''}
          onChange={(e) => onItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
          className={`w-full px-2 py-1.5 text-sm text-center border rounded ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-600 text-white' 
              : 'bg-white border-gray-200 text-gray-900'
          } focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none`}
        />
      </td>
      <td className="px-2 py-2">
        <input
          type="number"
          min="0"
          step="0.01"
          value={item.unit_price || ''}
          onChange={(e) => onItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
          className={`w-full px-2 py-1.5 text-sm text-right border rounded font-mono ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-600 text-white' 
              : 'bg-white border-gray-200 text-gray-900'
          } focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none`}
        />
      </td>
      <td className="px-2 py-2">
        <select
          value={item.tax_rate || 15}
          onChange={(e) => onItemChange(index, 'tax_rate', parseFloat(e.target.value))}
          className={`w-full px-2 py-1.5 text-sm text-center border rounded ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-600 text-white' 
              : 'bg-white border-gray-200 text-gray-900'
          } focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none`}
        >
          <option value={0}>0%</option>
          <option value={15}>15%</option>
          {taxRates.filter(t => t.rate !== 0 && t.rate !== 15).map(rate => (
            <option key={rate.id} value={rate.rate}>{rate.rate}%</option>
          ))}
        </select>
      </td>
      <td className="px-2 py-2 text-right">
        <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          R {formatCurrency(lineTotal)}
        </span>
      </td>
      <td className="px-2 py-2">
        <div className="flex flex-wrap items-center justify-center gap-1">
          <button
            type="button"
            onClick={() => onItemChange(index, 'is_optional', !item.is_optional)}
            title={item.is_optional ? 'Optional item' : 'Required item'}
            className={`p-1 rounded text-xs ${
              item.is_optional 
                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' 
                : isDarkMode ? 'text-gray-500 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'
            }`}
          >
            {item.is_optional ? 'Opt' : '—'}
          </button>
          <button
            type="button"
            onClick={() => onItemChange(index, 'is_required', !item.is_required)}
            title={item.is_required ? 'Marked as required in SLA' : 'Mark as required in SLA'}
            className={`p-1 rounded text-[10px] ${
              item.is_required
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                : isDarkMode ? 'text-gray-500 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'
            }`}
          >
            Req
          </button>
          <button
            type="button"
            onClick={() => onItemChange(index, 'quantity_editable', !item.quantity_editable)}
            title={item.quantity_editable ? 'Quantity can be adjusted after apply' : 'Lock quantity after apply'}
            className={`p-1 rounded text-[10px] ${
              item.quantity_editable
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                : isDarkMode ? 'text-gray-500 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'
            }`}
          >
            Qty
          </button>
          <button
            type="button"
            onClick={() => onItemChange(index, 'price_editable', !item.price_editable)}
            title={item.price_editable ? 'Price can be adjusted after apply' : 'Lock price after apply'}
            className={`p-1 rounded text-[10px] ${
              item.price_editable
                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                : isDarkMode ? 'text-gray-500 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'
            }`}
          >
            Price
          </button>
        </div>
      </td>
      <td className="px-2 py-2">
        <button onClick={() => onRemove(index)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded">
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
}
